'use client';

import { useState } from 'react';
import { VStack, HStack, Box } from '@terra/ui-styled-system';
import {
  Button,
  LegacyDialog,
  Field,
  IconButton,
  Text,
  Textarea,
} from '@terra/ui-design-system';
import {
  MessageAdd01Icon,
  ThumbsDownIcon,
  ThumbsUpIcon,
  Cancel01Icon,
} from '@terra/ui-icons';

const MAX_FEEDBACK_LENGTH = 2000;

type FeedbackRating = 'positive' | 'negative';
type SignalProvideFeedbackModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (params: { rating: FeedbackRating; feedback?: string }) => Promise<{
    success: boolean;
  }>;
  renderMode?: 'dialog' | 'inline';
};

export function SignalProvideFeedbackModal({
  isOpen,
  onClose,
  onSubmit,
  renderMode = 'dialog',
}: SignalProvideFeedbackModalProps) {
  const [feedback, setFeedback] = useState('');
  const [rating, setRating] = useState<FeedbackRating | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const remainingChars = MAX_FEEDBACK_LENGTH - feedback.length;
  const handleClose = () => {
    setFeedback('');
    setRating(null);
    setIsSubmitting(false);
    setSubmitError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!rating) {
      setSubmitError('Select approve or reject to continue');

      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await onSubmit({
        rating,
        feedback: feedback.trim() || undefined,
      });

      if (result.success) {
        handleClose();
      } else {
        setSubmitError('Failed to submit feedback. Please try again.');
      }
    } catch {
      setSubmitError('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const titleNode =
    renderMode === 'dialog' ? (
      <LegacyDialog.Title>Provide Feedback</LegacyDialog.Title>
    ) : (
      <Text fontWeight="semibold">Provide Feedback</Text>
    );
  const descriptionNode =
    renderMode === 'dialog' ? (
      <LegacyDialog.Description color="fg.muted">
        Share whether this signal should be approved or rejected.
      </LegacyDialog.Description>
    ) : (
      <Text fontSize="sm" color="fg.muted">
        Share whether this signal should be approved or rejected.
      </Text>
    );
  const content = (
    <VStack gap={6} p={6} alignItems="stretch">
      <HStack justifyContent="space-between" alignItems="flex-start">
        <VStack gap={1} alignItems="flex-start">
          <HStack gap={2} alignItems="center">
            <MessageAdd01Icon size={20} />
            {titleNode}
          </HStack>
          {descriptionNode}
        </VStack>
        <IconButton
          aria-label="Close"
          variant="ghost"
          size="sm"
          onClick={handleClose}
        >
          <Cancel01Icon size={16} />
        </IconButton>
      </HStack>

      <Field.Root width="full">
        <Field.Label>
          <Text fontSize="sm" fontWeight="medium">
            Signal Feedback
          </Text>
        </Field.Label>
        <HStack gap={2}>
          <Button
            variant={rating === 'positive' ? 'solid' : 'outline'}
            onClick={() => setRating('positive')}
            flex="1"
          >
            <HStack gap={2} alignItems="center">
              <ThumbsUpIcon size={14} />
              <Text>Approve</Text>
            </HStack>
          </Button>
          <Button
            variant={rating === 'negative' ? 'solid' : 'outline'}
            onClick={() => setRating('negative')}
            flex="1"
          >
            <HStack gap={2} alignItems="center">
              <ThumbsDownIcon size={14} />
              <Text>Reject</Text>
            </HStack>
          </Button>
        </HStack>
      </Field.Root>

      <Field.Root width="full">
        <Field.Label>
          <HStack justifyContent="space-between" width="full">
            <Text fontSize="sm" fontWeight="medium" color="fg.default">
              Additional Feedback (Optional)
            </Text>
            <Text
              fontSize="xs"
              color={remainingChars < 0 ? 'fg.error' : 'fg.muted'}
            >
              {remainingChars} characters remaining
            </Text>
          </HStack>
        </Field.Label>
        <Textarea
          value={feedback}
          onChange={(event) => setFeedback(event.target.value)}
          placeholder="Add more context for your decision (optional)"
          rows={6}
          resize="vertical"
          maxLength={MAX_FEEDBACK_LENGTH}
        />
        {submitError && (
          <Field.ErrorText>
            <Text fontSize="sm" color="fg.error">
              {submitError}
            </Text>
          </Field.ErrorText>
        )}
      </Field.Root>

      <HStack gap={2} justifyContent="flex-end" width="full">
        <Button variant="outline" onClick={handleClose} size="md">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting || remainingChars < 0}
          loading={isSubmitting}
          size="md"
        >
          Submit
        </Button>
      </HStack>
    </VStack>
  );

  if (renderMode === 'inline') {
    if (!isOpen) return null;

    return (
      <Box
        borderWidth="1px"
        borderColor="border.subtle"
        borderRadius="lg"
        bg="bg.default"
      >
        {content}
      </Box>
    );
  }

  return (
    <LegacyDialog.Root
      open={isOpen}
      onOpenChange={({ open }) => {
        if (!open) {
          handleClose();
        }
      }}
      lazyMount
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content maxWidth="600px" width="90vw">
          {content}
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}
